import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureUiError } from '../config/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureUiError(error, info.componentStack ?? undefined);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
          <p className="mt-2 max-w-md text-sm text-gray-600">
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <button type="button" onClick={this.handleRetry} className="btn-primary mt-6">
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
